import React, { useState, useEffect } from 'react';
import { updateUserProfile } from '../services/firestoreService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';

const Profile = ({ user }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        profilePicture: null
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [profilePictureUrl, setProfilePictureUrl] = useState('');

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
                dateOfBirth: user.dateOfBirth || '',
                gender: user.gender || '',
                address: user.address || '',
                city: user.city || '',
                state: user.state || '',
                zipCode: user.zipCode || '',
                emergencyContactName: user.emergencyContactName || '',
                emergencyContactPhone: user.emergencyContactPhone || '',
                profilePicture: null
            });
            setProfilePictureUrl(user.profilePictureUrl || '');
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileUpload = async (file) => {
        if (!file) return null;

        const maxSize = 2 * 1024 * 1024; // 2MB
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];

        if (file.size > maxSize) {
            setError('Profile picture must be less than 2MB');
            return null;
        }

        if (!allowedTypes.includes(file.type)) {
            setError('Profile picture must be JPG, PNG, or GIF');
            return null;
        }

        try {
            setUploadProgress(0);
            const storageRef = ref(storage, `profile-pictures/${user.uid}/${file.name}`);

            // Simulate upload progress
            const interval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(interval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 100);

            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            setUploadProgress(100);
            clearInterval(interval);

            setTimeout(() => setUploadProgress(0), 1000);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            setError('Failed to upload profile picture');
            return null;
        }
    };

    const handleProfilePictureChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const imageUrl = await handleFileUpload(file);
            if (imageUrl) {
                setProfilePictureUrl(imageUrl);
                setFormData(prev => ({ ...prev, profilePicture: file }));
            }
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }

        if (formData.phone && !/^\+?[\d\s-()]+$/.test(formData.phone)) {
            errors.phone = 'Phone number is invalid';
        }

        if (formData.emergencyContactPhone && !/^\+?[\d\s-()]+$/.test(formData.emergencyContactPhone)) {
            errors.emergencyContactPhone = 'Emergency contact phone is invalid';
        }

        return errors;
    };

    const handleSave = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setError('Please fix the errors in the form');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            let profilePictureUrlToUpdate = profilePictureUrl;

            // Upload new profile picture if selected
            if (formData.profilePicture) {
                profilePictureUrlToUpdate = await handleFileUpload(formData.profilePicture);
            }

            // Prepare update data
            const updateData = {
                name: formData.name.trim(),
                phone: formData.phone.trim(),
                dateOfBirth: formData.dateOfBirth,
                gender: formData.gender,
                address: formData.address.trim(),
                city: formData.city.trim(),
                state: formData.state.trim(),
                zipCode: formData.zipCode.trim(),
                emergencyContactName: formData.emergencyContactName.trim(),
                emergencyContactPhone: formData.emergencyContactPhone.trim(),
            };

            // Only include profile picture URL if it was updated
            if (profilePictureUrlToUpdate) {
                updateData.profilePictureUrl = profilePictureUrlToUpdate;
            }

            const result = await updateUserProfile(user.uid, updateData);

            if (result.success) {
                setSuccess('Profile updated successfully!');
                setIsEditing(false);
                // Reset form data to reflect the updated user data
                setFormData(prev => ({ ...prev, profilePicture: null }));
            } else {
                setError(result.error || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            setError('An unexpected error occurred while updating your profile');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            dateOfBirth: user.dateOfBirth || '',
            gender: user.gender || '',
            address: user.address || '',
            city: user.city || '',
            state: user.state || '',
            zipCode: user.zipCode || '',
            emergencyContactName: user.emergencyContactName || '',
            emergencyContactPhone: user.emergencyContactPhone || '',
            profilePicture: null
        });
        setProfilePictureUrl(user.profilePictureUrl || '');
        setError('');
        setSuccess('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(part => part.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const membershipStats = [
        {
            label: 'Total Contributions',
            value: user.totalContributions || 0,
            icon: '💝',
            color: 'primary'
        },
        {
            label: 'Total Amount Given',
            value: `$${((user.totalContributions || 0) * 50).toLocaleString()}`,
            icon: '💰',
            color: 'green'
        },
        {
            label: 'Available Balance',
            value: `$${user.availableBalance || 0}`,
            icon: '🏦',
            color: 'blue'
        },
        {
            label: 'Support Received',
            value: user.supportReceived || 0,
            icon: '🛡️',
            color: 'purple'
        }
    ];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Error and Success Messages */}
            {error && (
                <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                    </div>
                </div>
            )}

            {success && (
                <div className="rounded-md bg-green-50 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">{success}</h3>
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
                        <p className="text-gray-600 mt-1">Manage your personal information and account settings</p>
                    </div>
                    <button
                        onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                        disabled={loading}
                        className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : isEditing ? (
                            'Save Changes'
                        ) : (
                            'Edit Profile'
                        )}
                    </button>
                </div>

                {/* Profile Picture Section */}
                <div className="flex items-center space-x-6 mb-8">
                    <div className="relative">
                        {profilePictureUrl ? (
                            <img
                                src={profilePictureUrl}
                                alt="Profile"
                                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                            />
                        ) : (
                            <div className="w-24 h-24 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold border-4 border-white shadow-lg">
                                {getInitials(user.name)}
                            </div>
                        )}

                        {isEditing && (
                            <>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleProfilePictureChange}
                                    className="hidden"
                                    id="profilePicture"
                                />
                                <label
                                    htmlFor="profilePicture"
                                    className="absolute bottom-0 right-0 bg-primary-500 text-white p-2 rounded-full cursor-pointer hover:bg-primary-600 transition-colors shadow-lg"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </label>
                            </>
                        )}
                    </div>

                    {uploadProgress > 0 && (
                        <div className="flex-1 max-w-xs">
                            <div className="text-sm text-gray-600 mb-1">Uploading... {uploadProgress}%</div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    <div className="flex-1">
                        <h2 className="text-xl font-semibold text-gray-900">{user.name}</h2>
                        <p className="text-gray-600">{user.email}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Member since {formatDate(user.membershipDate)}
                        </p>
                        <div className="flex items-center space-x-2 mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : user.status === 'pending_verification'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
              }`}>
                {user.status === 'active' ? '✅ Verified' :
                    user.status === 'pending_verification' ? '⏳ Pending Verification' :
                        '❌ Suspended'}
              </span>
                        </div>
                    </div>
                </div>

                {/* Membership Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {membershipStats.map((stat, index) => (
                        <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl mb-2">{stat.icon}</div>
                            <div className={`text-2xl font-bold text-${stat.color}-600`}>
                                {stat.value}
                            </div>
                            <div className="text-sm text-gray-600">{stat.label}</div>
                        </div>
                    ))}
                </div>

                {/* Profile Form */}
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                placeholder="Enter your full name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                disabled={true} // Email shouldn't be editable for now
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                placeholder="Enter your email"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                placeholder="Enter your phone number"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date of Birth
                            </label>
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Gender
                            </label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="">Select gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                                <option value="prefer-not-to-say">Prefer not to say</option>
                            </select>
                        </div>
                    </div>

                    {/* Address Section */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Street Address
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    placeholder="Enter your street address"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        placeholder="City"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        name="state"
                                        value={formData.state}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        placeholder="State"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ZIP Code
                                    </label>
                                    <input
                                        type="text"
                                        name="zipCode"
                                        value={formData.zipCode}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        placeholder="ZIP Code"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Emergency Contact Section */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Emergency Contact Name
                                </label>
                                <input
                                    type="text"
                                    name="emergencyContactName"
                                    value={formData.emergencyContactName}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    placeholder="Full name of emergency contact"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Emergency Contact Phone
                                </label>
                                <input
                                    type="tel"
                                    name="emergencyContactPhone"
                                    value={formData.emergencyContactPhone}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    placeholder="Phone number of emergency contact"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons when Editing */}
                    {isEditing && (
                        <div className="flex space-x-4 pt-6 border-t border-gray-200">
                            <button
                                onClick={handleCancel}
                                disabled={loading}
                                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={loading}
                                className="flex-1 bg-primary-500 text-white px-4 py-3 rounded-lg hover:bg-primary-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Account Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Member ID
                        </label>
                        <p className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded border">
                            {user.uid}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Membership Status
                        </label>
                        <p className="text-gray-900">
                            {user.status === 'active' ? 'Active Member' :
                                user.status === 'pending_verification' ? 'Pending Verification' :
                                    'Suspended'}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Join Date
                        </label>
                        <p className="text-gray-900">{formatDate(user.membershipDate)}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last Updated
                        </label>
                        <p className="text-gray-900">{formatDate(user.updatedAt)}</p>
                    </div>
                </div>
            </div>

            {/* ID Verification Status */}
            {(user.idDocuments || user.beneficiaries) && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h3>

                    {user.idDocuments && (
                        <div className="mb-6">
                            <h4 className="font-medium text-gray-900 mb-3">ID Documents</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center space-x-3">
                  <span className={
                      user.idDocuments.primaryId === 'verified' ? 'text-green-500' :
                          user.idDocuments.primaryId === 'pending' ? 'text-yellow-500' : 'text-red-500'
                  }>
                    {user.idDocuments.primaryId === 'verified' ? '✅' :
                        user.idDocuments.primaryId === 'pending' ? '⏳' : '❌'}
                  </span>
                                    <span>Primary ID: {user.idDocuments.primaryId}</span>
                                </div>
                                <div className="flex items-center space-x-3">
                  <span className={
                      user.idDocuments.secondaryId === 'verified' ? 'text-green-500' :
                          user.idDocuments.secondaryId === 'pending' ? 'text-yellow-500' : 'text-red-500'
                  }>
                    {user.idDocuments.secondaryId === 'verified' ? '✅' :
                        user.idDocuments.secondaryId === 'pending' ? '⏳' : '❌'}
                  </span>
                                    <span>Secondary ID: {user.idDocuments.secondaryId}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {user.beneficiaries && user.beneficiaries.length > 0 && (
                        <div>
                            <h4 className="font-medium text-gray-900 mb-3">Beneficiaries</h4>
                            <p className="text-gray-600">
                                You have {user.beneficiaries.length} beneficiary{user.beneficiaries.length !== 1 ? 's' : ''} registered.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Profile;